import React, { useState } from "react";
import "./services.css";
import v2 from "../../img/v2.png";
import v4 from "../../img/v4.png";
import e from "../../img/e.png";
import o from "../../img/o.png";
import webD from "../../img/webD.png";
import tech2 from "../../img/tech2.png";
import v from "../../img/v.png";
import tech from "../../img/tech.png";
import Card from "../Card/Card";
import MohamedAliResume from "./Mohamed Ali Resume.pdf";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react/dist/iconify.js";

const serviceGroups = [
  {
    title: "Full-Stack Web Applications",
    icon: "mdi:layers-outline",
    tags: [
      "End-to-End Builds (React/Next.js + Node/Express)",
      "RESTful & GraphQL API Development",
      "SQL & NoSQL Database Design (PostgreSQL, MongoDB)",
      "Cloud Deployment (Vercel, Render, VPS)",
    ],
  },
  {
    title: "AI-Powered Products",
    icon: "mdi:robot-outline",
    tags: [
      "LLM & AI API Integration (OpenAI, Anthropic)",
      "Intelligent Chat & Automation Features",
      "Retrieval-Augmented Generation (RAG) Pipelines",
      "AI-Assisted Workflows & Data Processing",
    ],
  },
  {
    title: "Frontend Engineering",
    icon: "mdi:code-tags",
    tags: [
      "Custom React Dashboards (Admin Panels / Analytics UI)",
      "Responsive, Mobile-First Interfaces",
      "Figma/PSD-to-React Conversion",
      "Reusable Component Systems",
    ],
  },
  {
    title: "Authentication & User Management",
    icon: "mdi:shield-account-outline",
    tags: [
      "Secure Auth (JWT + Refresh Tokens + Roles)",
      "Role-Based Access (Admin/User Permissions)",
      "Email Verification + Password Reset",
      "Team Accounts (Multi-User Support)",
    ],
  },
  {
    title: "Payments & SaaS Infrastructure",
    icon: "mdi:credit-card-outline",
    tags: [
      "Stripe Subscriptions & Webhooks",
      "Plan Upgrades / Usage-Based Billing",
      "SaaS MVPs (Auth, Billing, Dashboard)",
      "Product & Usage Analytics",
    ],
  },
  {
    title: "Performance & Maintenance",
    icon: "mdi:speedometer",
    tags: [
      "React / Node / Express Bug Fixing",
      "Performance Tuning & Code Splitting",
      "Codebase Refactors & Audits",
      "Ongoing Support & Enhancements",
    ],
  },
];

const BuildAccordion = ({ groups }) => {
  const [openIndex, setOpenIndex] = useState(-1);

  return (
    <div className="build-accordion">
      {groups.map((service, index) => {
        const isOpen = openIndex === index;
        return (
          <div key={service.title} className={`build-item${isOpen ? " open" : ""}`}>
            <button
              type="button"
              className="build-item-header"
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
              aria-expanded={isOpen}
            >
              <Icon icon={service.icon} className="build-item-icon" />
              <span className="build-item-title">{service.title}</span>
              <Icon icon="mdi:chevron-down" className="build-item-chevron" />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  className="build-item-body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <div className="service-tags">
                    {service.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

const Services = () => {
  const transition = { duration: 1, type: "spring" };

  return (
    <div className="services">
      <motion.div
        className="services-intro"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={transition}
      >
        <span className="section-eyebrow">What I Offer</span>
        <h2 className="servicesSubheading">
          Full-Stack Web Development &amp; AI-Powered Products
        </h2>
      </motion.div>

      <motion.div
        className="services-block"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.02 }}
        transition={transition}
      >
        <h3 className="services-block-label">What I Build</h3>
        <BuildAccordion groups={serviceGroups} />
      </motion.div>

      <motion.div
        className="services-block"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.02 }}
        transition={{ ...transition, delay: 0.1 }}
      >
        <h3 className="services-block-label">Core Strengths</h3>
        <div className="cards">
          <Card
            emoji={v4}
            heading={"Technologies"}
            details={
              "Full Stack Development, JavaScript, TypeScript, React, Next.js, REST APIs, Cloud Services"
            }
            link="https://roadmap.sh/full-stack"
          />
          <Card
            emoji={v2}
            heading={"Performance"}
            details={
              "SEO Best Practices, Performance Tuning, Integration, Deployment, Hosting"
            }
            link="https://web.dev/learn/performance"
          />
          <Card
            emoji={tech2}
            heading={"Security"}
            details={
              "Personal Data Encryption, Cybersecurity Defense, Secure Authentication"
            }
            link="https://owasp.org/www-project-top-ten/"
          />
          <Card
            emoji={o}
            heading={"Speed Optimization"}
            details={
              "Fast Loading Times, Code Splitting, Lazy Loading, Image Optimization"
            }
            link="https://www.cloudflare.com/learning/performance/speed-up-a-website/"
          />
          <Card
            emoji={e}
            heading={"Efficiency"}
            details={
              "Efficient Code, Low Resource Consumption, High Uptime, Scalable Architecture"
            }
            link="https://12factor.net/"
          />
          <Card
            emoji={webD}
            heading={"Innovation"}
            details={
              "Cutting-edge Technologies, Creative Solutions, Future-proof Design, Adaptive Strategies"
            }
            link="https://www.technologyreview.com/2026/08/10/1141384/ai-agents-for-science/"
          />
          <Card
            emoji={v}
            heading={"AI & Automation"}
            details={
              "LLM Integration, Intelligent Automation, AI-Assisted Features, Data-Driven Workflows"
            }
            link="https://n8n.io/"
          />
          <Card
            emoji={tech}
            heading={"Scalability & Integration"}
            details={
              "Cloud Infrastructure, Microservices, Third-Party API Integration, System Architecture"
            }
            link="https://aws.amazon.com/microservices/"
          />
        </div>
      </motion.div>

      <div className="buttonDiv">
        <a href={MohamedAliResume} download className="serviceBtn">
          <span>Download CV</span>
          <Icon icon="line-md:download-loop" width="1.5rem" height="1.5rem" />
        </a>
      </div>
    </div>
  );
};

export default Services;
